import type { LuckyWheelDependencies, LuckyWheelObservable } from '../types';
import { preloadLayoutsAssets } from '../../../views/preloadLayoutsAssets';
import { LuckyWheelPopup, LuckyWheelPopupState } from '../views/LuckyWheelPopup';
import {
  getNextSpinTime,
  getTotalFreeSpins,
  hasBonusFreeSpin,
  hasRewardedFreeSpinsToUse,
  isAdSpinAvailable,
  isFreeSpinAvailable,
  useBonusFreeSpin,
} from '../LuckyWheelUtils';
import { performLuckyWheelSpin } from './performLuckyWheelSpin';
import { luckyWheelRewards } from '../LuckyWheelConstants';
import { AdResponse } from '../../../app/tracking/trackingModules/avo/Avo';
import { InventoryItem } from '../../../app/resources/types';

export interface OpenLuckyWheelConfig {
  dependencies: LuckyWheelDependencies;
  toggleCoinCounterUpdate: (value: boolean, checkForUpdate?: boolean) => void;
}

export async function openLuckyWheelPopup(config: OpenLuckyWheelConfig): Promise<void> {
  if (!(await preloadLayoutsAssets(LuckyWheelPopup.requiredLayouts, true))) return Promise.resolve();

  const { dependencies } = config;
  const { popupsController, observable, tracking } = dependencies;
  const { luckyWheel } = observable.getValue();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { resolve, promise } = Promise.withResolvers<void>();

  const popup = new LuckyWheelPopup();
  const state = getWheelState(dependencies);
  popup.refreshView({
    state,
    spinCount: getRemainingSpins(state, observable),
    nextSpinTime: getNextSpinTime(luckyWheel.lastFreeSpinTime),
  });

  popup.onCloseClicked.connect(async () => {
    await popupsController.closeActivePopup();
    config.toggleCoinCounterUpdate(true);
    resolve();
  });

  popup.onFreeSpinButtonClicked.connect(async () => {
    if (isFreeSpinAvailable(observable)) {
      if (hasBonusFreeSpin(observable)) {
        useBonusFreeSpin(observable);
      } else if (hasRewardedFreeSpinsToUse(observable)) {
        dependencies.useRewardedFreeSpin();
      }

      void handleWheelSpinFlow(popup, dependencies, false, config);
    }
  });

  popup.onAdSpinButtonClicked.connect(async () => {
    if (isAdSpinAvailable(observable)) {
      const response = await dependencies.getAdsPlayer()?.showRewardedAd('lucky-wheel');
      if (response === AdResponse.SUCCESS) {
        void handleWheelSpinFlow(popup, dependencies, true, config);
      }
    }
  });

  popup.onClaimButtonClicked.connect(async () => {
    const { luckyWheel: updatedLuckyWheel } = observable.getValue();

    let isCoinReward = false;
    if (updatedLuckyWheel.lastWonRewardIndex) {
      isCoinReward = luckyWheelRewards[updatedLuckyWheel.lastWonRewardIndex].reward?.resource === InventoryItem.Coin;
    }

    config.toggleCoinCounterUpdate(true, isCoinReward);
    const newState = getWheelState(dependencies);
    await popup.animateClaimFlow({
      state: newState,
      spinCount: getRemainingSpins(newState, observable),
      nextSpinTime: getNextSpinTime(updatedLuckyWheel.lastAdSpinTime),
    });
  });

  popup.onSpinResetTimeout.connect(async () => {
    await popup.animateRefreshFlow({
      state: getWheelState(dependencies),
      spinCount: observable.getValue().luckyWheel.rewardedFreeSpins,
      nextSpinTime: getNextSpinTime(observable.getValue().luckyWheel.lastAdSpinTime),
    });
    resolve();
  });

  void popupsController.addPopup(popup);

  tracking.trackUIInteraction({
    elementId: 'luckyWheel',
    contextName: 'mainMenu',
    action: 'Click',
  });

  return promise;
}

function getWheelState(dependencies: LuckyWheelDependencies): LuckyWheelPopupState {
  const { getAdsPlayer, observable } = dependencies;

  if (isFreeSpinAvailable(observable)) return LuckyWheelPopupState.FreeSpin;
  if (!!getAdsPlayer() && isAdSpinAvailable(observable)) return LuckyWheelPopupState.AdSpin;
  return LuckyWheelPopupState.NoSpin;
}

function getRemainingSpins(state: LuckyWheelPopupState, observable: LuckyWheelObservable): number {
  const { luckyWheel } = observable.getValue();

  if (state === LuckyWheelPopupState.FreeSpin) {
    return getTotalFreeSpins(observable);
  }

  return luckyWheel.adSpinCount;
}

async function handleWheelSpinFlow(
  popup: LuckyWheelPopup,
  dependencies: LuckyWheelDependencies,
  isAdSpin: boolean,
  config: OpenLuckyWheelConfig,
): Promise<void> {
  config.toggleCoinCounterUpdate(false);
  const result = performLuckyWheelSpin(dependencies, isAdSpin);
  if (result.reward) {
    void popup.animateWheelSpinFlow(result.index, result.reward, !!luckyWheelRewards[result.index].isJackpot);
  }
}
